import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { imageSrc } from "@/lib/images";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, X, ImageIcon, Crop } from "lucide-react";
import { ImageCropDialog } from "@/components/ImageCropDialog";

const MAX_BYTES = 5 * 1024 * 1024;

/** Headshot / logo picker: upload a file (optionally cropped) or paste a link. */
export function ImageUploadField({
  label = "Photo", value, onChange, context = {}, round = false, testId = "image",
  cropAspect = null,
  hint = "PNG, JPG or WebP up to 5 MB — or paste a hosted link.",
}) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null); // { src, name }
  const src = imageSrc(value);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/files", form, {
        params: { ...context, folder_key: "images" },
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(`/api/files/${data.id}/download`);
      toast.success("Image attached");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload that image");
    } finally {
      setBusy(false);
    }
  };

  const pick = (file) => {
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > MAX_BYTES) { toast.error("That image is over 5 MB — try a smaller one"); return; }
    if (cropAspect && file.type !== "image/svg+xml") {
      setPending({ src: URL.createObjectURL(file), name: file.name });
      return;
    }
    upload(file);
  };

  const recrop = async () => {
    try {
      let objectUrl;
      if (String(value).startsWith("/api/")) {
        const { data } = await api.get(String(value).replace(/^\/api/, ""), { responseType: "blob" });
        objectUrl = URL.createObjectURL(data);
      } else {
        const res = await fetch(src, { mode: "cors" });
        objectUrl = URL.createObjectURL(await res.blob());
      }
      setPending({ src: objectUrl, name: "headshot.jpg" });
    } catch {
      toast.error("Can't re-crop that image — upload the original file instead");
    }
  };

  const closeCrop = () => {
    if (pending?.src) URL.revokeObjectURL(pending.src);
    setPending(null);
  };

  return (
    <div className="space-y-2" data-testid={`${testId}-field`}>
      <Label className="text-xs">{label}</Label>
      <div className="flex items-start gap-4">
        <div data-testid={`${testId}-preview`}
             className={`w-20 h-20 shrink-0 border border-dashed border-border bg-muted/40 flex items-center justify-center overflow-hidden ${round ? "rounded-full" : "rounded-xl"}`}>
          {src
            ? <img src={src} alt={label} className="w-full h-full object-cover" />
            : <ImageIcon className="w-5 h-5 text-muted-foreground/50" />}
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <input ref={fileRef} type="file" className="hidden" data-testid={`${testId}-file-input`}
                   accept="image/png,image/jpeg,image/webp,image/svg+xml"
                   onChange={(e) => pick(e.target.files?.[0])} />
            <Button type="button" size="sm" variant="outline" disabled={busy}
                    data-testid={`${testId}-upload-btn`} onClick={() => fileRef.current?.click()}>
              <Upload className="w-3.5 h-3.5 mr-1.5" />
              {busy ? "Uploading…" : src ? "Replace" : "Upload"}
            </Button>
            {src && cropAspect && (
              <Button type="button" size="sm" variant="outline" data-testid={`${testId}-recrop-btn`}
                      onClick={recrop}>
                <Crop className="w-3.5 h-3.5 mr-1.5" /> Re-crop
              </Button>
            )}
            {src && (
              <Button type="button" size="sm" variant="ghost" data-testid={`${testId}-clear-btn`}
                      onClick={() => onChange("")}>
                <X className="w-3.5 h-3.5 mr-1" /> Remove
              </Button>
            )}
          </div>
          <Input data-testid={`${testId}-url-input`} placeholder="https://…"
                 value={value && !String(value).startsWith("/api/") ? value : ""}
                 onChange={(e) => onChange(e.target.value)} />
          <p className="text-[11px] text-muted-foreground">{hint}</p>
        </div>
      </div>

      <ImageCropDialog open={Boolean(pending)} src={pending?.src} aspect={cropAspect || 1}
                       round={round} filename={pending?.name || "headshot.jpg"}
                       testId={`${testId}-crop`} onCancel={closeCrop}
                       onCropped={(file) => { closeCrop(); upload(file); }} />
    </div>
  );
}
